import { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { networkPillLabel } from "@/lib/networkLabels";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Command, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import DateRangePresets from "@/components/DateRangePresets";
import TimezoneSelect, { detectBrowserTimezone } from "@/components/TimezoneSelect";
import FeedPartnerSelect, { appendNetworkParam, appendRouteFromParam } from "@/components/FeedPartnerSelect";
import {
  Funnel, DownloadSimple, CircleNotch, MagnifyingGlass, Check,
  CaretDown, ArrowsClockwise, X, Cursor, Globe, Receipt, PaperPlaneRight,
  ArrowSquareOut, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { RSpinBlock } from "@/components/RSpin";

const ADMIN_CLICK_LOG_TZ_KEY = "admin_click_log_timezone";

function initialAdminTimezone() {
  const abbrev = {
    EST: "America/New_York", EDT: "America/New_York",
    CST: "America/Chicago", CDT: "America/Chicago",
    MST: "America/Denver", MDT: "America/Denver",
    PST: "America/Los_Angeles", PDT: "America/Los_Angeles",
  };
  try {
    const saved = localStorage.getItem(ADMIN_CLICK_LOG_TZ_KEY);
    if (saved) return abbrev[saved] || saved;
  } catch { /* ignore */ }
  return detectBrowserTimezone();
}

// Pretty labels + chip colors for upstream feed networks. Mirrors the
// palette used in AdminBrands / AdminBrandsSync so admins instantly
// recognise the source feed across screens.
const NETWORK_COLORS = {
  impact: "bg-slate-100 text-slate-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-fuchsia-50 text-fuchsia-700",
  siteplug: "bg-sky-50 text-sky-700",
  giddyup: "bg-teal-50 text-teal-800",
  yieldkit: "bg-fuchsia-50 text-fuchsia-700",
  shopnomix: "bg-lime-50 text-lime-700",
  pronomix: "bg-purple-50 text-purple-700",
  maxbounty: "bg-cyan-50 text-cyan-700",
  cashbackhive: "bg-teal-50 text-teal-700",
  fatcoupon: "bg-rose-50 text-rose-700",
  rga: "bg-violet-50 text-violet-700",
  rga_vishy: "bg-violet-50 text-violet-700",
  rga_ashok: "bg-pink-50 text-pink-700",
};

function NetworkPill({ network, testIdPrefix = "click-log-network" }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  const cls = NETWORK_COLORS[n] || "bg-slate-100 text-slate-700";
  const lbl = networkPillLabel(n);
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}
      data-testid={`${testIdPrefix}-${n}`}
    >
      {lbl}
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-slate-200 rounded-md p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-xl font-bold font-mono ${accent ? "text-[#002FA7]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function TrackerLinkCell({ url }) {
  if (!url) return <span className="text-slate-400 text-xs">—</span>;
  const copy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(
      () => toast.success("Tracking link copied"),
      () => toast.error("Copy failed"),
    );
  };
  return (
    <a
      href={url}
      onClick={copy}
      className="inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline whitespace-nowrap"
      title="Click to copy tracking link"
      data-testid="click-log-tracker-link"
    >
      <LinkSimple size={12} weight="bold" />
      Copy link
    </a>
  );
}

const PAGE_SIZE = 100;

const CLICK_KIND_OPTIONS = [
  { value: "valid", label: "Valid clicks" },
  { value: "all", label: "All clicks" },
  { value: "blocked", label: "Blocked only" },
];

export default function AdminClickLog() {
  // Filter state
  const [brand, setBrand] = useState("all");
  const [publisherId, setPublisherId] = useState("all");
  const [network, setNetwork] = useState("all");
  const [routeFrom, setRouteFrom] = useState("all");
  const [dateRange, setDateRange] = useState({ preset: "all", start: "", end: "" });
  const [search, setSearch] = useState("");
  const [clickKind, setClickKind] = useState("valid");
  const [page, setPage] = useState(1);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [remoteBrands, setRemoteBrands] = useState([]);
  const [brandSearching, setBrandSearching] = useState(false);
  const brandSearchReq = useRef(0);
  const didFetch = useRef(false);
  const [viewTimezone, setViewTimezone] = useState(initialAdminTimezone);

  // Data state
  const [filters, setFilters] = useState({ brands: [], publishers: [], networks: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [validTotal, setValidTotal] = useState(0);
  const [blockedTotal, setBlockedTotal] = useState(0);
  const [routedTotal, setRoutedTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ received: 0, elapsedMs: 0, total: 0 });

  // Detail drawer state — `detailRid` holds the row's RID we're inspecting.
  const [detailRid, setDetailRid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (row) => {
    if (!row || row.row_kind === "blocked") {
      toast.message(row?.conversion_status || "Blocked click — no RID minted");
      return;
    }
    const rid = row.rid;
    if (!rid) return;
    setDetailRid(rid);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await API.get(`/admin/click-log/${encodeURIComponent(rid)}/detail`);
      setDetail(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load click detail");
      setDetailRid(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setDetailRid(null);
    setDetail(null);
  };

  // `fresh` bypasses the server's filter-options cache (Refresh button), so a
  // publisher who just got their first clicks shows up in the dropdown.
  const fetchFilterOptions = useCallback(async (fresh = false) => {
    try {
      const { data } = await API.get("/admin/click-log/filter-options", {
        params: fresh ? { fresh: 1 } : undefined,
      });
      setFilters({
        brands: data?.brands || [],
        publishers: data?.publishers || [],
        networks: data?.networks || [],
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchClicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brand !== "all") params.set("brand", brand);
      if (publisherId !== "all") params.set("publisher_id", publisherId);
      appendNetworkParam(params, network);
      appendRouteFromParam(params, routeFrom);
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (viewTimezone) params.set("timezone", viewTimezone);
      if (search.trim()) params.set("search", search.trim());
      params.set("row_kind", clickKind);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await API.get(`/admin/click-log?${params.toString()}`);
      setRows(data?.data || []);
      setTotal(data?.total || 0);
      setValidTotal(data?.valid_total ?? data?.total ?? 0);
      setBlockedTotal(data?.blocked_total ?? 0);
      setRoutedTotal(data?.routed_total ?? 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load click log");
    } finally {
      setLoading(false);
    }
  }, [brand, publisherId, network, routeFrom, dateRange.start, dateRange.end, search, page, viewTimezone, clickKind]);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Reset to page 1 whenever a filter changes (not the page selector itself).
  useEffect(() => {
    setPage(1);
  }, [brand, publisherId, network, routeFrom, dateRange.start, dateRange.end, search, viewTimezone, clickKind]);

  // Debounce search so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { fetchClicks(); }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchClicks]);

  // ─── CSV export ────────────────────────────────────────────────────
  const downloadCsv = async () => {
    if (exporting) return;
    const startedAt = Date.now();
    setExporting(true);
    setExportProgress({ received: 0, elapsedMs: 0, total: 0 });

    const ticker = setInterval(() => {
      setExportProgress((s) => ({ ...s, elapsedMs: Date.now() - startedAt }));
    }, 250);

    try {
      const params = new URLSearchParams();
      if (brand !== "all") params.set("brand", brand);
      if (publisherId !== "all") params.set("publisher_id", publisherId);
      appendNetworkParam(params, network);
      appendRouteFromParam(params, routeFrom);
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (viewTimezone) params.set("timezone", viewTimezone);
      if (search.trim()) params.set("search", search.trim());
      params.set("row_kind", clickKind);
      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(`${baseURL}/admin/click-log/export.csv?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const totalHdr = parseInt(resp.headers.get("X-Total-Count") || "0", 10);
      setExportProgress((s) => ({ ...s, total: totalHdr }));

      const reader = resp.body.getReader();
      const chunks = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setExportProgress((s) => ({ ...s, received }));
      }
      const blob = new Blob(chunks, { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const kind = clickKind;
      a.download = `click-log-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${totalHdr.toLocaleString()} row${totalHdr === 1 ? "" : "s"} to CSV`);
    } catch (err) {
      console.error(err);
      toast.error("CSV export failed");
    } finally {
      clearInterval(ticker);
      setExporting(false);
    }
  };

  const stats = useMemo(() => {
    const conversions = rows.filter((r) => r.converted).length;
    const earnings = rows.reduce((s, r) => s + (r.earnings || 0), 0);
    const saleAmt = rows.reduce((s, r) => s + (r.sale_amount || 0), 0);
    return { conversions, earnings, saleAmt };
  }, [rows]);

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  const selectedBrandLabel = useMemo(() => {
    if (brand === "all") return "All Brands";
    const b = filters.brands.find((x) => x.brand_name === brand)
      || remoteBrands.find((x) => x.brand_name === brand);
    return b ? `${b.brand_name} (${b.clicks.toLocaleString()})` : brand;
  }, [brand, filters.brands, remoteBrands]);

  const visibleBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    const local = q
      ? filters.brands.filter((b) => (b.brand_name || "").toLowerCase().includes(q))
      : filters.brands;
    const seen = new Set(local.map((b) => (b.brand_name || "").toLowerCase()));
    const extra = remoteBrands.filter(
      (b) => b.brand_name && !seen.has(b.brand_name.toLowerCase()),
    );
    const merged = q ? [...local, ...extra] : local;
    return merged.slice(0, 100);
  }, [filters.brands, brandSearch, remoteBrands]);

  useEffect(() => {
    if (!brandPickerOpen) return undefined;
    const q = brandSearch.trim();
    if (q.length < 2) {
      setRemoteBrands([]);
      setBrandSearching(false);
      return undefined;
    }
    const id = ++brandSearchReq.current;
    setBrandSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: "50" });
        const { data } = await API.get(`/admin/click-log/brands/search?${params}`);
        if (brandSearchReq.current !== id) return;
        setRemoteBrands(Array.isArray(data?.brands) ? data.brands : []);
      } catch {
        if (brandSearchReq.current === id) setRemoteBrands([]);
      } finally {
        if (brandSearchReq.current === id) setBrandSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [brandSearch, brandPickerOpen]);

  return (
    <div className="p-8" data-testid="admin-click-log-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Audit / Tracking
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">
            Click Log
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Every click flowing through the platform — across publishers, brands, and feed partners.
            Date filters and timestamps use the selected timezone (defaults to your local system).
            Use the click type filter to include blocked tracker attempts (cap, pause, etc.).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchFilterOptions(true); fetchClicks(); }}
            disabled={loading}
            className="h-9 shrink-0"
            data-testid="click-log-refresh"
          >
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            className="h-9 shrink-0"
            data-testid="click-log-download-csv"
          >
            {exporting
              ? <><CircleNotch size={14} className="mr-1.5 animate-spin" /> Preparing…</>
              : <><DownloadSimple size={14} className="mr-1.5" /> Download CSV</>}
          </Button>
        </div>
      </div>

      {/* Export progress card */}
      {exporting && (
        <div
          className="mb-5 border border-slate-200 bg-slate-50 rounded-md p-3 flex items-center gap-3"
          data-testid="click-log-export-progress"
        >
          <CircleNotch size={18} className="text-[#002FA7] animate-spin shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-slate-700">
              Downloading {exportProgress.total > 0
                ? <>~<span className="font-mono">{exportProgress.total.toLocaleString()}</span> clicks</>
                : "click log"}…
            </div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex gap-3">
              <span>{(exportProgress.received / 1024).toFixed(1)} KB received</span>
              <span>{(exportProgress.elapsedMs / 1000).toFixed(1)}s elapsed</span>
            </div>
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Funnel size={16} className="text-slate-400" />

        {/* Brand picker — searchable Combobox. Top brands-by-volume load
            from filter-options; typing queries /brands/search so 1-click
            long-tail names (e.g. Coyuchi) still match. cmdk's built-in
            fuzzy filter is disabled — it was dropping exact substring
            hits. */}
        <Popover
          open={brandPickerOpen}
          onOpenChange={(next) => {
            setBrandPickerOpen(next);
            if (!next) {
              setBrandSearch("");
              setRemoteBrands([]);
              setBrandSearching(false);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              size="sm"
              className="w-64 h-9 text-sm font-normal justify-between"
              data-testid="click-log-brand-filter"
            >
              <span className="truncate">{selectedBrandLabel}</span>
              <CaretDown size={12} className="opacity-60 shrink-0 ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search brand…"
                value={brandSearch}
                onValueChange={setBrandSearch}
                data-testid="click-log-brand-search-input"
              />
              <CommandList className="max-h-80">
                {visibleBrands.length === 0 && brandSearch.trim() ? (
                  <div className="py-6 text-center text-sm text-slate-500" data-testid="click-log-brand-empty">
                    {brandSearching ? (
                      <span className="inline-flex items-center gap-1.5">
                        <CircleNotch size={12} className="animate-spin" /> Searching…
                      </span>
                    ) : (
                      "No brand with clicks."
                    )}
                  </div>
                ) : null}
                <CommandGroup>
                  <CommandItem
                    value="all-brands"
                    onSelect={() => { setBrand("all"); setBrandPickerOpen(false); }}
                  >
                    <Check size={14} className={`mr-2 ${brand === "all" ? "opacity-100" : "opacity-0"}`} />
                    All Brands
                  </CommandItem>
                  {visibleBrands.map((b) => (
                    <CommandItem
                      key={b.brand_name}
                      value={b.brand_name}
                      onSelect={() => { setBrand(b.brand_name); setBrandPickerOpen(false); }}
                      data-testid={`click-log-brand-option-${b.brand_name}`}
                    >
                      <Check size={14} className={`mr-2 ${brand === b.brand_name ? "opacity-100" : "opacity-0"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{b.brand_name}</div>
                        {b.network && (
                          <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                            {networkPillLabel(b.network)}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-slate-400 ml-2">{b.clicks.toLocaleString()}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {brandSearch.trim().length < 2 && filters.brands.length > 100 && (
                  <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                    Showing top brands by clicks — type to search all
                  </div>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Publisher filter */}
        <Select value={publisherId} onValueChange={setPublisherId}>
          <SelectTrigger className="w-56 h-9 text-sm" data-testid="click-log-publisher-filter">
            <SelectValue placeholder="All Publishers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Publishers</SelectItem>
            {filters.publishers.map((p) => (
              <SelectItem key={p.publisher_id} value={p.publisher_id}>
                {p.email} ({p.clicks.toLocaleString()})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FeedPartnerSelect
          value={network}
          onChange={setNetwork}
          testId="click-log-network-filter"
          allLabel="All networks"
          menuLabel="Landed on"
          extraOptions={(filters.networks || []).map((n) => ({ value: n, label: n }))}
        />

        <FeedPartnerSelect
          value={routeFrom}
          onChange={setRouteFrom}
          testId="click-log-route-from-filter"
          allLabel="All route-from"
          menuLabel="Route from"
          extraOptions={(filters.networks || []).map((n) => ({ value: n, label: n }))}
        />

        <DateRangePresets
          preset={dateRange.preset}
          start={dateRange.start}
          end={dateRange.end}
          onChange={setDateRange}
          testId="click-log-date-range"
        />

        <div className="w-64" data-testid="click-log-timezone-wrap">
          <TimezoneSelect
            value={viewTimezone}
            onChange={(tz) => {
              setViewTimezone(tz);
              try { localStorage.setItem(ADMIN_CLICK_LOG_TZ_KEY, tz); } catch { /* ignore */ }
            }}
            listEndpoint="/admin/timezones"
            testId="click-log-timezone-select"
            className="h-9"
          />
        </div>

        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search RID / SubId / Order ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm pl-8 w-64"
            data-testid="click-log-search-input"
          />
        </div>

        <Select value={clickKind} onValueChange={setClickKind}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="click-log-kind-filter">
            <SelectValue placeholder="Click type" />
          </SelectTrigger>
          <SelectContent>
            {CLICK_KIND_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`click-log-kind-${opt.value}`}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className={`grid gap-3 mb-4 ${clickKind === "all" ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-7" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"}`}>
        <StatCard label="Total Rows" value={total.toLocaleString()} />
        <StatCard label="Routed" value={routedTotal.toLocaleString()} />
        {clickKind === "all" && (
          <>
            <StatCard label="Valid Clicks" value={validTotal.toLocaleString()} />
            <StatCard label="Blocked Attempts" value={blockedTotal.toLocaleString()} accent />
          </>
        )}
        {clickKind === "blocked" && (
          <StatCard label="Blocked Attempts" value={blockedTotal.toLocaleString()} accent />
        )}
        <StatCard label="Conversions (page)" value={stats.conversions.toLocaleString()} />
        <StatCard label="Sale Amount (page)" value={`$${stats.saleAmt.toFixed(2)}`} />
        {clickKind !== "blocked" && (
          <StatCard label="Earnings (page)" value={`$${stats.earnings.toFixed(2)}`} accent />
        )}
      </div>

      {/* Table */}
      {loading ? (
        <RSpinBlock label="Loading click log" height={300} />
      ) : rows.length === 0 ? (
        <div
          className="border border-slate-200 rounded-md p-12 text-center text-sm text-slate-500"
          data-testid="click-log-empty"
        >
          No clicks match the current filters.
          {network !== "all" && routeFrom !== "all" && (
            /* The one combination that reliably looks broken but is not.
               "Landed on" matches where a click ENDED UP; a click routed
               away from a network no longer carries that network, so
               pairing the two asks for traffic routed INTO it — which is
               usually nothing. Say so rather than leaving a bare zero. */
            <div className="mt-3 text-xs text-slate-500 max-w-lg mx-auto leading-relaxed">
              <strong className="text-slate-700">Landed on</strong> is where the
              click ended up, <strong className="text-slate-700">Route from</strong>{" "}
              is where it started. Together they mean “routed{" "}
              <em>from</em> {routeFrom} <em>into</em> {network}”.
              <br />
              To see traffic routed <em>away from</em>{" "}
              {routeFrom}, set <strong className="text-slate-700">Landed on</strong>{" "}
              back to “All networks”.
              <button
                type="button"
                onClick={() => setNetwork("all")}
                className="ml-2 underline text-slate-700 hover:text-slate-900"
                data-testid="click-log-empty-clear-network"
              >
                Clear it
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="click-log-table">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Date</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Feed</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Route from</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">SubId1</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">SubId2</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tracker</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">RID (Click ID)</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sale</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Earnings</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isBlocked = r.row_kind === "blocked";
                  const rowKey = isBlocked ? `blocked-${r.hit_id || i}` : `${r.rid}-${i}`;
                  return (
                  <tr
                    key={rowKey}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${isBlocked ? "bg-rose-50/40" : "cursor-pointer"}`}
                    onClick={() => openDetail(r)}
                    data-testid={isBlocked ? `click-log-blocked-row-${r.hit_id || i}` : `click-log-row-${r.rid}`}
                  >
                    <td className="px-3 py-2.5 text-xs text-slate-700 font-mono whitespace-nowrap">
                      {(r.click_date || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-medium text-slate-900">
                      {r.brand_name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <NetworkPill network={r.network} />
                        {(r.offer_type || "").toLowerCase() === "cpc" && (
                          <span
                            className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800 border border-teal-200"
                            title="CPC click event — no conversion required"
                            data-testid={`click-log-cpc-${r.rid || r.click_id}`}
                          >
                            CPC
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <NetworkPill network={r.route_from} testIdPrefix="click-log-route-from" />
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-700">
                      <div className="truncate max-w-[180px]">
                        {r.publisher_email || (
                          r.sub_id1
                            ? <span className="text-slate-400" title="subId1 does not match any publisher account">No publisher · {r.sub_id1}</span>
                            : "—"
                        )}
                      </div>
                      {r.publisher_company && (
                        <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{r.publisher_company}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-slate-600">{r.sub_id1 || "—"}</td>
                    <td className="px-3 py-2.5 text-xs font-mono text-slate-600 truncate max-w-[140px]">{r.sub_id2 || "—"}</td>
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <TrackerLinkCell url={r.original_url || r.tracker_url} />
                    </td>
                    <td className="px-3 py-2.5 text-[11px] font-mono text-slate-500">{r.rid || (isBlocked ? "—" : "—")}</td>
                    <td className="px-3 py-2.5 text-xs text-right font-mono">
                      {r.sale_amount > 0 ? `$${r.sale_amount.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-right font-mono font-semibold text-[#002FA7]">
                      {r.earnings > 0 ? `$${r.earnings.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        isBlocked
                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                          : r.converted
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {r.conversion_status}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200"
              data-testid="click-log-pagination"
            >
              <div className="text-xs text-slate-500" data-testid="click-log-page-info">
                Showing <span className="font-mono font-semibold text-slate-700">{showingFrom.toLocaleString()}–{showingTo.toLocaleString()}</span> of <span className="font-mono font-semibold text-slate-700">{total.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                  data-testid="click-log-prev-page"
                >
                  Previous
                </Button>
                <span className="text-xs text-slate-500 font-mono px-2">Page {page} / {totalPages}</span>
                <Button
                  variant="outline" size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="click-log-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click detail drawer — full forensic dossier for one RID. */}
      <Sheet open={!!detailRid} onOpenChange={(o) => !o && closeDetail()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto p-0"
          data-testid="click-log-detail-drawer"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 bg-slate-50">
            <SheetTitle className="font-mono text-sm break-all">{detailRid || "—"}</SheetTitle>
            <SheetDescription>
              Full audit trail · click → tracker → conversion → postbacks
            </SheetDescription>
          </SheetHeader>

          {detailLoading || !detail ? (
            <div className="p-6">
              <RSpinBlock label="Loading audit trail" height={300} />
            </div>
          ) : (
            <DetailBody detail={detail} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Detail drawer body. Five stacked cards: Click summary · Tracker hits ·
// Conversion · Postbacks · Unified timeline. Kept as a sibling
// component so the main page renders independently when the drawer is
// closed.
// ──────────────────────────────────────────────────────────────────────
function DetailBody({ detail }) {
  const { click, raw_click, tracker_hits, action, postbacks, timeline } = detail;
  return (
    <div className="p-6 space-y-5">
      <DetailSection icon={<Cursor size={16} weight="bold" />} title="Click">
        <KV label="Date" value={(click.click_date || "").slice(0, 19).replace("T", " ")} />
        <KV label="Brand" value={`${click.brand_name || "—"}${click.network ? ` (${click.network})` : ""}`} />
        <KV label="Route from" value={click.route_from ? <NetworkPill network={click.route_from} testIdPrefix="click-log-detail-route-from" /> : "—"} />
        <KV label="Publisher" value={`${click.publisher_email || "—"}${click.publisher_company ? ` · ${click.publisher_company}` : ""}`} />
        <KV label="SubId1" value={click.sub_id1 || "—"} mono />
        <KV label="SubId2" value={click.sub_id2 || "—"} mono />
        <KV label="SubId3" value={click.sub_id3 || "—"} mono />
        <KV label="Tracker URL" value={click.tracker_url ? <TrackerLinkCell url={click.tracker_url} /> : "—"} />
        <KV label="Original URL" value={click.original_url ? <TrackerLinkCell url={click.original_url} /> : "—"} />
        <KV label="IP" value={raw_click.ip || "—"} mono />
        <KV label="User Agent" value={raw_click.user_agent || "—"} small />
        <KV label="Referer" value={raw_click.referer || "—"} small />
      </DetailSection>

      <DetailSection
        icon={<Globe size={16} weight="bold" />}
        title={`Tracker Hits (${tracker_hits.length})`}
      >
        {tracker_hits.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No raw tracker hit recorded — click was likely seeded from a postback or upstream sync.
          </p>
        ) : (
          tracker_hits.map((h, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 mb-2 last:mb-0 bg-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {h.outcome || "—"}
                </span>
                <span className="text-[10px] font-mono text-slate-400">
                  {(h.hit_at || "").slice(0, 19).replace("T", " ")}
                </span>
              </div>
              <KV label="SubId raw" value={h.sub_id1_raw || "—"} mono />
              <KV label="SubId resolved" value={h.sub_id1_resolved || "—"} mono />
              <KV label="Brand input" value={h.brand_id_input || "—"} mono />
              <KV label="Query" value={formatQuery(h.raw_query)} small mono />
            </div>
          ))
        )}
      </DetailSection>

      <DetailSection icon={<Receipt size={16} weight="bold" />} title="Conversion">
        {!action ? (
          <p className="text-xs text-slate-400 italic">No conversion fired for this click (yet).</p>
        ) : (
          <>
            <KV label="Action ID" value={action.action_id || "—"} mono />
            <KV label="State" value={action.state || "—"} />
            <KV label="Event Date" value={(action.event_date || "").slice(0, 19).replace("T", " ")} />
            <KV label="Sale Amount" value={`$${Number(action.amount || 0).toFixed(2)}`} />
            <KV label="Payout" value={`$${Number(action.payout || 0).toFixed(2)}`} />
            <KV label="Order ID" value={action.order_id || "—"} mono />
            <KV label="Customer" value={`${action.customer_city || "—"}, ${action.customer_country || "—"}`} />
            <KV label="Promo Code" value={action.promo_code || "—"} mono />
          </>
        )}
      </DetailSection>

      <DetailSection
        icon={<PaperPlaneRight size={16} weight="bold" />}
        title={`Postbacks (${postbacks.length})`}
      >
        {postbacks.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No postback events recorded.</p>
        ) : (
          postbacks.map((p, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 mb-2 last:mb-0 bg-white">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                  {p.direction || "?"} · {p.source || "?"}
                </span>
                <span className="text-[10px] font-mono text-slate-400">
                  {(p.received_at || "").slice(0, 19).replace("T", " ")}
                </span>
              </div>
              <pre className="text-[10px] font-mono text-slate-600 bg-slate-50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(p.params || {}, null, 2)}
              </pre>
            </div>
          ))
        )}
      </DetailSection>

      <DetailSection icon={<ArrowSquareOut size={16} weight="bold" />} title="Timeline">
        <ol className="border-l-2 border-slate-200 pl-4 space-y-3">
          {timeline.map((e, i) => (
            <li key={i} className="relative">
              <span
                className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white ${
                  e.kind === "conversion"
                    ? "bg-emerald-500"
                    : e.kind === "postback"
                    ? "bg-[#002FA7]"
                    : e.kind === "tracker_hit"
                    ? "bg-amber-500"
                    : "bg-slate-400"
                }`}
              />
              <div className="text-xs font-semibold text-slate-900">{e.title}</div>
              <div className="text-[10px] text-slate-500 font-mono">
                {(e.at || "").slice(0, 19).replace("T", " ")}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">{e.detail}</div>
            </li>
          ))}
        </ol>
      </DetailSection>
    </div>
  );
}

function DetailSection({ icon, title, children }) {
  return (
    <section>
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
        {icon} {title}
      </h3>
      <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
        {children}
      </div>
    </section>
  );
}

// `tracker_hits.raw_query` is the publisher's inbound query params, stored
// as an OBJECT (routers/tracker.py stores `raw_query: raw_params`). Passing
// it straight to KV threw React error #31 — "objects are not valid as a
// React child" — with keys {subId1, clickid, ref}, which took down the whole
// click-log page through the error boundary whenever an admin opened a click
// whose hit carried query params.
//
// Rendered back as the query string the publisher actually sent, which is
// what an admin reading this panel is trying to see anyway.
function formatQuery(raw) {
  if (!raw) return "—";
  if (typeof raw === "string") return raw || "—";
  if (typeof raw !== "object") return String(raw);
  const parts = Object.entries(raw).map(
    ([k, v]) => `${k}=${v == null ? "" : typeof v === "object" ? JSON.stringify(v) : v}`
  );
  return parts.length ? parts.join("&") : "—";
}

function KV({ label, value, mono, small }) {
  // Defence in depth: one unexpected object anywhere in this panel used to
  // blank the entire page rather than that single row. A detail view is for
  // diagnosing odd data, so it has to survive odd data.
  const safe =
    value != null && typeof value === "object" && !isValidElement(value)
      ? JSON.stringify(value)
      : value;
  return (
    <div className="flex gap-3 py-1 text-xs">
      <div className="w-28 shrink-0 text-slate-500">{label}</div>
      <div
        className={`flex-1 min-w-0 break-all ${mono ? "font-mono" : ""} ${
          small ? "text-[10px] text-slate-500" : "text-slate-800"
        }`}
      >
        {safe}
      </div>
    </div>
  );
}
